import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRecoilState } from "recoil";
import i18n from "src/i18n";
import {
  mapFoodicsInventoryItemToImportPayload,
  useFoodicsMenuEntities,
  useImportFoodicsMenuEntities,
} from "src/api/integrations/foodics";
import { isProductionItem } from "src/types/foodics";
import type { FoodicsInventoryItemEntity } from "src/types/foodics";
import { notificationsState } from "src/store/notifications";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";
import { NoData } from "src/shared/components/tables/no-data";

type InventoryItemsStepProps = {
  onBack: () => void;
  onFinish: () => void;
};

export const InventoryItemsStep = ({
  onBack,
  onFinish,
}: InventoryItemsStepProps) => {
  const { t } = useTranslation("integrations");
  const { t: generalT } = useTranslation("general");
  const [notifications, setNotifications] = useRecoilState(notificationsState);
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(100);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  // Ids already defaulted to selected once — unchecking must survive page changes.
  const seenIdsRef = useRef<Set<string>>(new Set());
  // Row payloads must stay available for import even after paging away.
  const rowsCacheRef = useRef<Map<string, FoodicsInventoryItemEntity>>(
    new Map()
  );

  const { data, isLoading } = useFoodicsMenuEntities({
    include_inventory: 1,
    inventory_page: page + 1,
    inventory_per_page: perPage,
    products_per_page: 1,
    categories_per_page: 1,
  });
  const { mutate: importItems, status: importStatus } =
    useImportFoodicsMenuEntities();

  // Production items (has ingredients) are imported in the Production items step.
  const inventoryItems = (data?.data?.inventory_items ?? []).filter(
    (row) => !isProductionItem(row)
  );
  const pagination = data?.data?.inventory_pagination ?? null;
  const fetchFailed = data?.data?.inventory_fetch_failed === true;
  const totalCount = pagination?.total ?? inventoryItems.length;
  const currentLang = i18n.language || "en";

  const getLocalized = (text?: { en?: string; ar?: string }): string =>
    text?.[currentLang as "en" | "ar"] || text?.en || text?.ar || "-";

  const formatLevel = (value?: string | number | null): string =>
    value != null && value !== "" ? String(value) : "—";

  // All not-yet-imported items are selected by default (screenshot behavior)
  useEffect(() => {
    const newIds: string[] = [];
    inventoryItems.forEach((row) => {
      if (!row.external_id) return;
      rowsCacheRef.current.set(row.external_id, row);
      if (!seenIdsRef.current.has(row.external_id)) {
        seenIdsRef.current.add(row.external_id);
        if (!row.already_imported) {
          newIds.push(row.external_id);
        }
      }
    });
    if (newIds.length > 0) {
      setSelectedIds((prev) => [...prev, ...newIds]);
    }
  }, [inventoryItems]);

  useEffect(() => {
    if (importStatus === "success") {
      onFinish();
    } else if (importStatus === "error") {
      setNotifications([
        ...notifications,
        { type: "error", message: t("menuImport.importError") },
      ]);
    }
  }, [importStatus]);

  const toggleItem = (id: string | null) => {
    if (!id) return;
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  // Already-imported items stay selectable so a re-sync can update their
  // Foodics-sourced fields (levels, cost, unit, name).
  const selectableVisibleIds = inventoryItems
    .filter((row) => !!row.external_id)
    .map((row) => row.external_id as string);
  const allVisibleSelected =
    selectableVisibleIds.length > 0 &&
    selectableVisibleIds.every((id) => selectedIds.includes(id));
  const someVisibleSelected = selectableVisibleIds.some((id) =>
    selectedIds.includes(id)
  );

  const toggleSelectAllVisible = () => {
    if (allVisibleSelected) {
      setSelectedIds((prev) =>
        prev.filter((id) => !selectableVisibleIds.includes(id))
      );
      return;
    }
    setSelectedIds((prev) => [
      ...prev,
      ...selectableVisibleIds.filter((id) => !prev.includes(id)),
    ]);
  };

  const handleImportAndFinish = () => {
    const payload = selectedIds
      .map((id) => rowsCacheRef.current.get(id))
      .filter(
        (row): row is FoodicsInventoryItemEntity => !!row && !!row.external_id
      )
      .map((row) => mapFoodicsInventoryItemToImportPayload(row));

    if (payload.length === 0) {
      onFinish();
      return;
    }
    importItems({
      categories: [],
      products: [],
      inventory_items: payload,
    });
  };

  return (
    <Stack gap={2}>
      <Box>
        <Typography fontSize={16} fontWeight={600} color="#101828">
          {t("foodicsWorkspace.editImport.itemsStepTitle")}
        </Typography>
        <Typography fontSize={12} color="#667085">
          {t("foodicsWorkspace.editImport.itemsHint")}
        </Typography>
      </Box>

      {fetchFailed && (
        <Alert severity="error">{t("menuImport.inventoryFetchFailed")}</Alert>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allVisibleSelected}
                  indeterminate={someVisibleSelected && !allVisibleSelected}
                  onChange={toggleSelectAllVisible}
                  disabled={selectableVisibleIds.length === 0}
                />
              </TableCell>
              {[
                t("menuImport.table.sku"),
                t("menuImport.table.name"),
                t("menuImport.table.kind"),
                t("menuImport.table.minLevel"),
                t("menuImport.table.maxLevel"),
              ].map((item) => (
                <TableCell
                  key={item}
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    lineHeight: "18px",
                    color: "#475467",
                    bgcolor: "#F9FAFB",
                  }}
                >
                  {item}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          {isLoading ? (
            <TableLoadingSkeleton rowsLength={5} cellsLength={6} />
          ) : (
            <TableBody>
              {inventoryItems.map((row, index) => (
                <TableRow key={row.external_id ?? `inv-${index}-${row.sku}`}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={
                        !!row.external_id &&
                        selectedIds.includes(row.external_id)
                      }
                      onChange={() => toggleItem(row.external_id)}
                      disabled={!row.external_id}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography fontSize={14} fontWeight={600}>
                      {row.sku || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography fontSize={14}>
                        {getLocalized(row.name)}
                      </Typography>
                      {row.already_imported && (
                        <Chip
                          size="small"
                          color="success"
                          label={t("menuImport.imported")}
                        />
                      )}
                      {!row.already_imported && row.is_new && (
                        <Chip
                          size="small"
                          color="warning"
                          label={t("menuImport.new")}
                        />
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={
                        isProductionItem(row)
                          ? t("menuImport.kindProduction")
                          : t("menuImport.kindRaw")
                      }
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography fontSize={14}>
                      {formatLevel(row.minimum_level)}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography fontSize={14}>
                      {formatLevel(row.maximum_level)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          )}
        </Table>
        {!isLoading && inventoryItems.length === 0 && <NoData />}
      </TableContainer>

      <TablePagination
        component="div"
        labelRowsPerPage={generalT("rowsPerPage")}
        count={totalCount}
        page={Math.max((pagination?.current ?? page + 1) - 1, 0)}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={pagination?.per ?? perPage}
        onRowsPerPageChange={(event) => {
          const value = parseInt(event.target.value, 10);
          setPerPage(value);
          setPage(0);
        }}
        rowsPerPageOptions={[25, 50, 100, 200]}
      />

      {/* Footer */}
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={2}
        pt={2}
        borderTop="1px solid #EAECF0"
      >
        <Typography fontSize={14} color="#667085">
          {t("foodicsWorkspace.editImport.selectedOf", {
            selected: selectedIds.length,
            total: seenIdsRef.current.size,
          })}
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="tertiary"
            onClick={onBack}
            disabled={importStatus === "pending"}
          >
            {generalT("back")}
          </Button>
          <Button
            variant="contained"
            disabled={importStatus === "pending"}
            startIcon={
              importStatus === "pending" ? (
                <CircularProgress size={16} color="inherit" />
              ) : null
            }
            onClick={handleImportAndFinish}
          >
            {t("foodicsWorkspace.editImport.importAndFinish")}
          </Button>
        </Stack>
      </Box>
    </Stack>
  );
};
